import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, Trash2, Loader2, Image, Printer } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import AlbumCover from '@/components/albums/AlbumCover';
import OrderPhotoPrintDialog from '@/components/albums/OrderPhotoPrintDialog';
import BulkPhotoUploader from '@/components/albums/BulkPhotoUploader';
import PhotoModal from '@/components/albums/PhotoModal';

const API = API_BASE;

export default function AlbumDetailPage() {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'member';
  const canPrint = user?.role === 'admin';
  
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [printPhoto, setPrintPhoto] = useState(null);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [viewerId, setViewerId] = useState(null);

  const photoSrc = (fileId) => `${API}/files/${fileId}`;

  const fetchAlbum = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/albums/${albumId}`, { withCredentials: true });
      setAlbum(response.data);
    } catch (error) {
      toast.error('Album not found');
      navigate('/albums');
    } finally {
      setLoading(false);
    }
  }, [albumId, navigate]);

  useEffect(() => {
    fetchAlbum();
  }, [fetchAlbum]);

  const handleDeleteAlbum = async () => {
    if (!window.confirm('Are you sure you want to delete this album and all its photos?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/albums/${albumId}`, { withCredentials: true });
      toast.success('Album deleted');
      navigate('/albums');
    } catch (error) {
      toast.error('Failed to delete album');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!album) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="album-detail-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/albums')}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-stone-900">{album.name}</h1>
            {album.description && (
              <p className="text-stone-500">{album.description}</p>
            )}
          </div>
          {canEdit && (
            <>
              <Button
                onClick={() => setUploaderOpen(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="add-photos-btn"
              >
                <Plus className="w-5 h-5 mr-2" /> Add Photos
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleDeleteAlbum}
                disabled={deleting}
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                data-testid="delete-album-btn"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </>
          )}
        </div>

        {/* Photos Grid */}
        {album.photos && album.photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {album.photos.map((photo, index) => (
              <button
                type="button"
                key={photo.id}
                onClick={() => setViewerId(photo.file_id)}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-stone-100 text-left"
                data-testid={`photo-${index}`}
              >
                <AlbumCover fileId={photo.file_id} />
                {canPrint && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPrintPhoto({ id: photo.file_id, src: photoSrc(photo.file_id) });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setPrintPhoto({ id: photo.file_id, src: photoSrc(photo.file_id) });
                      }
                    }}
                    className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-stone-800 text-xs font-medium shadow-md hover:bg-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer"
                    data-testid={`print-photo-${index}`}
                    aria-label="Print this photo"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    Print
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="card-soft text-center py-12" data-testid="empty-album">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">No photos yet</h2>
            <p className="text-stone-500 mb-6">
              {canEdit ? 'Add photos to this album' : 'Photos will appear here once added.'}
            </p>
            {canEdit && (
              <Button
                onClick={() => setUploaderOpen(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Photos
              </Button>
            )}
          </div>
        )}
      </main>

      <MobileNav />

      <BulkPhotoUploader
        open={uploaderOpen}
        onOpenChange={setUploaderOpen}
        albumId={albumId}
        onComplete={fetchAlbum}
      />

      <PhotoModal
        fileId={viewerId}
        open={!!viewerId}
        onOpenChange={(o) => { if (!o) setViewerId(null); }}
        onPrint={canPrint ? (fid) => {
          setViewerId(null);
          setPrintPhoto({ id: fid, src: photoSrc(fid) });
        } : null}
      />

      <OrderPhotoPrintDialog
        open={!!printPhoto}
        onOpenChange={(open) => { if (!open) setPrintPhoto(null); }}
        fileId={printPhoto?.id}
        previewUrl={printPhoto?.src}
      />
    </div>
  );
}
